import _ from 'lodash'
import workflowApi from '@/api/workflow'
import store from '@/store'
import { generateFormHandlerMutations } from '@/helpers/forms'

const emptySummary = () => ({
  runnable: 0,
  suspended: 0,
  complete: 0,
  terminated: 0,
  stale: 0,
  staleAfterHours: 0,
  terminatedAtOnce: 0,
  definitions: [],
})

const state = {
  list: [],
  totalCount: 0,
  loading: false,
  error: null,

  summary: emptySummary(),
  summaryLoaded: false,
  summaryError: null,

  terminating: false,

  terminateResult: null,

  filters: {
    pageNumber: 1,
    pageSize: 25,
    status: null,
    definition: null,
    dateFrom: null,
    dateTo: null,
    onlyStale: false,
  },
}

const errorText = (error, fallback) => error.response?.data?.message || fallback

const terminatedText = ({ terminated, failed, remaining }) => [
  `Отменено workflow: ${terminated}`,
  failed > 0 ? `Не удалось отменить: ${failed} — в этот момент шло выполнение` : '',
  remaining > 0 ? `Осталось зависших: ${remaining} — нажмите кнопку ещё раз` : '',
].filter(x => x).join('. ')

const terminatedStatus = ({ failed, remaining }) => {
  if (failed > 0) return 'error'

  return remaining > 0 ? 'info' : 'success'
}

const getters = {}

const actions = {
  fetchList({ commit }) {
    commit('LIST_LOADING')

    return workflowApi.getAll(_.omitBy(state.filters, _.isNil))
      .then(({ data }) => commit('SET_LIST', data))
      .catch(error => commit('SET_LIST_ERROR', errorText(error, 'Не удалось прочитать список workflow')))
  },

  fetchSummary({ commit }) {
    return workflowApi.getSummary()
      .then(({ data }) => commit('SET_SUMMARY', data))
      .catch(error => commit('SET_SUMMARY_ERROR', errorText(error, 'Не удалось прочитать счётчики workflow')))
  },

  terminateStale({ commit, dispatch }) {
    commit('TERMINATING', true)
    commit('SET_TERMINATE_RESULT', null)

    return workflowApi.terminateStale()
      .then(({ data }) => commit('SET_TERMINATE_RESULT', {
        type: terminatedStatus(data),
        text: terminatedText(data),
      }))
      .catch(error => commit('SET_TERMINATE_RESULT', {
        type: 'error',
        text: errorText(error, 'Не удалось отменить зависшие workflow'),
      }))
      .finally(() => {
        commit('TERMINATING', false)
        dispatch('fetchSummary')

        if (state.filters.pageNumber !== 1) {
          commit('FORM_UPDATE_FIELD', { key: 'filters.pageNumber', value: 1 })

          return
        }

        dispatch('fetchList')
      })
  },
}

const mutations = {
  LIST_LOADING(state) {
    state.loading = true
    state.error = null
  },
  SET_LIST(state, { items, metadata }) {
    state.list = items
    state.totalCount = metadata.totalItemCount
    state.loading = false
  },
  SET_LIST_ERROR(state, text) {
    state.list = []
    state.totalCount = 0
    state.error = text
    state.loading = false
  },
  SET_SUMMARY(state, data) {
    state.summary = { ...emptySummary(), ...data }
    state.summaryLoaded = true
    state.summaryError = null
  },

  SET_SUMMARY_ERROR(state, text) {
    state.summary = { ...emptySummary(), definitions: state.summary.definitions }
    state.summaryLoaded = false
    state.summaryError = text
  },
  TERMINATING(state, value) {
    state.terminating = value
  },
  SET_TERMINATE_RESULT(state, result) {
    state.terminateResult = result
  },
  ...generateFormHandlerMutations(state),
}

const watchers = [
  [
    ({ workflow: { filters } }) => [
      filters.status, filters.definition, filters.dateFrom, filters.dateTo, filters.onlyStale,
    ].join('|'),
    () => store.commit('workflow/FORM_UPDATE_FIELD', { key: 'filters.pageNumber', value: 1 }),
  ],
  [
    state => state.workflow.filters,
    _.debounce(async () => await store.dispatch('workflow/fetchList'), 250),
    { immediate: false, deep: true },
  ],
]

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  watchers,
}
